import type { Middleware, MiddlewareState } from '@floating-ui/react';

interface StickyHideOptions {
  boundary: HTMLElement;
  padding?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  stickyThreshold?: number;
}

/**
 * Floating UI middleware that provides sticky positioning behavior at scroll container edges.
 *
 * When the floating element reaches the edge of the visible boundary area, it enters "sticky mode"
 * where its position is frozen relative to the container. The middleware continues to return the
 * frozen position coordinates until the element is no longer at the edge or the scroll threshold
 * is exceeded.
 *
 * @param options - Configuration options
 * @param options.boundary - The scroll container element to track
 * @param options.padding - Padding from container edges (default: 0 for all sides)
 * @param options.stickyThreshold - Scroll distance (px) before hiding the element (default: 0)
 *
 * @returns Middleware that provides:
 * - `x`, `y` - Frozen absolute coordinates when sticky (for position: absolute in scroll container)
 * - `data.isSticky` - Boolean indicating if currently in sticky mode
 * - `data.stickyEdge` - Object indicating which edge triggered sticky mode (only top/bottom supported)
 * - `data.stickyViewportPosition` - Frozen viewport coordinates (for position: fixed in document.body)
 * - `data.stickyVisualPosition` - Frozen visual position relative to container viewport
 * - `data.referenceHidden` - Boolean indicating if element should hide (scroll exceeded threshold)
 *
 * **How it works:**
 * 1. Calculates if floating element is at the edge of the visible container area
 * 2. When edge is reached, captures and freezes position in three coordinate systems:
 *    - Absolute: for elements with position: absolute in scroll container
 *    - Viewport: for elements with position: fixed in document.body
 *    - Visual: relative to container's visible area
 * 3. Returns frozen coordinates on subsequent calls while at edge
 * 4. Exits sticky mode when element is no longer at edge
 * 5. Optionally hides element if scroll distance exceeds stickyThreshold
 *
 * **Requirements:**
 * - Use with `strategy: 'absolute'` in useFloating config
 * - The boundary element (scroll container) MUST have `position: relative` or `position: absolute`
 *   to establish a positioning context for the absolutely positioned floating element
 *
 * **Use cases:**
 * - Keeping floating elements visible at container edges during scroll
 * - Dual-render patterns where one version tracks and another displays
 * - Smooth sticky behavior without JavaScript position updates
 */
export function stickyHideWithScroll(options: StickyHideOptions): Middleware {
  const { boundary, padding = {}, stickyThreshold = 0 } = options;

  // Closure state persists across middleware calls
  const state = {
    isAtEdge: false,
    scrollWhenAtEdge: { top: 0, left: 0 },
    frozenVisualPosition: null as { x: number; y: number } | null,
    frozenAbsolutePosition: null as { x: number; y: number } | null,
    stickyEdge: null as { top: boolean; bottom: boolean } | null,
  };

  return {
    name: 'stickyHideWithScroll',
    async fn(middlewareState: MiddlewareState) {
      const { x, y, elements } = middlewareState;

      if (isNaN(x) || isNaN(y))
        return {
          data: { referenceHidden: false, isSticky: false },
        };

      const pad = {
        top: padding.top ?? 0,
        right: padding.right ?? 0,
        bottom: padding.bottom ?? 0,
        left: padding.left ?? 0,
      };

      const floatingRect = elements.floating.getBoundingClientRect();
      const boundaryRect = boundary.getBoundingClientRect();

      // For absolute positioned elements in scroll container,
      // we need to work with scroll-adjusted coordinates
      const scrollTop = boundary.scrollTop;
      const scrollLeft = boundary.scrollLeft;

      // Calculate if element would be at edge of visible boundary area
      const visualTop = y - scrollTop;
      const visualLeft = x - scrollLeft;

      const isAtTopEdge = visualTop < pad.top;
      const isAtBottomEdge =
        visualTop + floatingRect.height > boundaryRect.height - pad.bottom;

      const isAtEdge = isAtTopEdge || isAtBottomEdge;

      // No sticky threshold - hide immediately
      if (stickyThreshold === 0) {
        return {
          data: { referenceHidden: isAtEdge, isSticky: false },
        };
      }

      // Read current scroll position
      const currentScroll = {
        top: scrollTop,
        left: scrollLeft,
      };

      // Entering sticky mode
      if (isAtEdge && !state.isAtEdge) {
        state.isAtEdge = true;
        state.scrollWhenAtEdge = currentScroll;

        // Capture which edges triggered sticky mode
        state.stickyEdge = {
          top: isAtTopEdge,
          bottom: isAtBottomEdge,
        };

        // Store the clamped VISUAL position (relative to visible area)
        const clampedVisualX = Math.max(
          pad.left,
          Math.min(
            visualLeft,
            boundaryRect.width - pad.right - floatingRect.width
          )
        );
        const clampedVisualY = Math.max(
          pad.top,
          Math.min(
            visualTop,
            boundaryRect.height - pad.bottom - floatingRect.height
          )
        );

        state.frozenVisualPosition = { x: clampedVisualX, y: clampedVisualY };

        // Calculate and cache absolute position - this won't change while sticky
        const absoluteX = scrollLeft + clampedVisualX;
        const absoluteY = scrollTop + clampedVisualY;

        state.frozenAbsolutePosition = { x: absoluteX, y: absoluteY };

        // Calculate viewport position for fixed positioning
        const stickyViewportPosition = {
          x: boundaryRect.left + clampedVisualX,
          y: boundaryRect.top + clampedVisualY,
        };

        return {
          x: absoluteX,
          y: absoluteY,
          data: {
            referenceHidden: false,
            isSticky: true,
            stickyEdge: state.stickyEdge,
            stickyVisualPosition: state.frozenVisualPosition,
            stickyViewportPosition,
          },
        };
      }

      // In sticky mode - return cached absolute position
      if (isAtEdge && state.isAtEdge) {
        // Only track vertical scroll since we only handle top/bottom edges
        const scrollDelta = Math.abs(
          currentScroll.top - state.scrollWhenAtEdge.top
        );

        const shouldHide = scrollDelta > stickyThreshold;

        // Calculate viewport position for fixed positioning
        const stickyViewportPosition = {
          x: boundaryRect.left + state.frozenVisualPosition!.x,
          y: boundaryRect.top + state.frozenVisualPosition!.y,
        };

        // Return the frozen absolute position from when we entered sticky mode
        return {
          x: state.frozenAbsolutePosition!.x,
          y: state.frozenAbsolutePosition!.y,
          data: {
            referenceHidden: shouldHide,
            isSticky: true,
            stickyEdge: state.stickyEdge,
            stickyVisualPosition: state.frozenVisualPosition,
            stickyViewportPosition,
          },
        };
      }

      // Exiting sticky mode
      if (!isAtEdge && state.isAtEdge) {
        state.isAtEdge = false;
        state.frozenVisualPosition = null;
        state.frozenAbsolutePosition = null;
        state.stickyEdge = null;
      }

      // Always clamp x-axis to prevent element from going off-screen horizontally
      const clampedVisualX = Math.max(
        pad.left,
        Math.min(
          visualLeft,
          boundaryRect.width - pad.right - floatingRect.width
        )
      );
      const clampedAbsoluteX = scrollLeft + clampedVisualX;

      return {
        x: clampedAbsoluteX,
        data: { referenceHidden: false, isSticky: false },
      };
    },
  };
}
